import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./common/Card";
import Button from "./common/Button";
import Spinner from "./common/Spinner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "./common/Dialog";
import { CalendarDays, Clock, ArrowLeft } from "lucide-react";
import useBrandTranslation from "../hooks/useBrandTranslation";
import {
  previewCancelFromEmail,
  cancelFromEmail,
} from "../apis/api.appointments";
import customToast from "./common/Toaster";
import * as utils from "../utils/utils";
import { formatDateDMY, formatClockTimeRange } from "../lib/ScheduleUtils";

export default function CancelAppointmentFromEmail() {
  const { t } = useBrandTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(
        t(
          "app.appointments.invalidCancelLink",
          "El enlace de cancelación no es válido."
        )
      );
      setLoading(false);
      return;
    }

    previewCancelFromEmail(token)
      .then((data) => setPreview(data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token, t]);

  const handleConfirmCancel = async () => {
    setCancelling(true);
    try {
      await cancelFromEmail(token);
      setCancelled(true);
      setShowConfirm(false);
      customToast(
        t("app.appointments.cancelSuccess", "Turno cancelado correctamente"),
        "success"
      );
    } catch (err) {
      customToast(
        err.message ||
          t("app.appointments.cancelError", "Error al cancelar el turno"),
        "danger"
      );
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-lg mx-auto">
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => utils.goBack()}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.backHome", "Volver al Inicio")}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {t("app.appointments.cancelFromEmailTitle", "Cancelar turno")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <p className="text-red-600 text-center py-6">{error}</p>
            )}

            {cancelled && (
              <div className="text-center py-6 space-y-4">
                <p className="text-green-700 font-medium">
                  {t(
                    "app.appointments.cancelSuccess",
                    "Turno cancelado correctamente"
                  )}
                </p>
                <Button onClick={() => navigate("/patient/appointments")}>
                  {t("app.appointments.myAppointments", "Mis Turnos")}
                </Button>
              </div>
            )}

            {!error && !cancelled && preview && (
              <div className="space-y-4">
                {!preview.can_cancel ? (
                  <p className="text-gray-600 text-center py-4">
                    {t(
                      "app.appointments.alreadyCancelled",
                      "Este turno ya fue cancelado o no puede modificarse."
                    )}
                  </p>
                ) : (
                  <>
                    <p className="text-gray-600">
                      {t(
                        "app.appointments.cancelFromEmailDesc",
                        "Estás por cancelar el siguiente turno:"
                      )}
                    </p>
                    <div className="border rounded-lg p-4 space-y-2 bg-gray-50">
                      <div className="flex items-center gap-2 text-sm">
                        <CalendarDays className="w-4 h-4 text-gray-500" />
                        <span>{formatDateDMY(preview.appointment.date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span>
                          {formatClockTimeRange(
                            preview.appointment.start_time,
                            preview.appointment.end_time
                          )}
                        </span>
                      </div>
                      <p className="text-sm font-medium">
                        {preview.appointment.doctor_name}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={() => setShowConfirm(true)}
                    >
                      {t("app.appointments.cancelAppointment", "Cancelar turno")}
                    </Button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="w-[95vw] max-w-md rounded-xl p-6">
          <DialogTitle className="text-lg font-bold text-gray-900 mb-4">
            {t(
              "app.appointments.confirmCancelGeneric",
              "¿Estás seguro que deseas cancelar?"
            )}
          </DialogTitle>
          <p className="text-gray-600 text-sm mb-6">
            {t(
              "app.appointments.confirmCancelDesc",
              "Esta acción liberará el horario para otros pacientes."
            )}
          </p>
          <div className="flex gap-3 justify-end">
            <DialogClose asChild>
              <Button variant="outline" disabled={cancelling}>
                {t("common.cancel", "Cancelar")}
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={cancelling}
            >
              {cancelling
                ? "..."
                : t("app.appointments.confirmYes", "Sí, cancelar")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
